import { Dispatch, SetStateAction } from 'react';

import { ApolloError } from 'apollo-client';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  BenefitType,
  IBenefit,
  IBenefitSwap,
  ILoyaltyUserQuery,
  IOffersFragment,
  IRewardFragment,
  useLoyaltyUserQuery,
} from 'generated/graphql-gateway';
import { IAppliedOffer } from 'generated/rbi-graphql';
import { INotificationDetailsFragment } from 'generated/sanity-graphql';

import { LoyaltyEngineRewards } from '../types';

export type LoyaltyUser = ILoyaltyUserQuery['loyaltyUser'];

export type AvailableRewards = {
  [id: string]: IRewardFragment;
};

export interface IUseLoyalty {
  loyaltyEnabled: boolean;
  loyaltyLoading: boolean;
  loyaltyUser: LoyaltyUser | null;
  refetchLoyaltyUser: ReturnType<typeof useLoyaltyUserQuery>['refetch'];
}

export type UseLoyaltyRewards = (loyaltyUser: LoyaltyUser | null) => IUseLoyaltyRewards;

interface IAppliedReward {
  timesApplied: number;
  rewardId: string;
  pointCost: number;
  rewardBenefitId: string;
}

export interface IAppliedRewards {
  [cartId: string]: IAppliedReward;
}
export interface IParsedLoyaltyReward {
  cartId: string;
  rewardId: string;
  timesApplied: number;
}

export interface IParseAppliedRewards {
  appliedRewardsArray: IParsedLoyaltyReward[];
  timesRewardApplied: { [rewardId: string]: number };
}

export interface IUseLoyaltyOffers {
  getLoyaltyOffers: (cartEntries: ICartEntry[], appliedRewards: IAppliedRewards) => void;
  upsizeAvailable: boolean;
  offers: IOffersFragment[];
  resetUpsizeAvailability: () => void;
  appliedOffers: IAppliedOffer[];
  applyOffer: (offer: IAppliedOffer) => void;
  updateAppliedOffers: (cartEntry: ICartEntry) => void;
  resetAppliedOffers: () => void;
}

export interface IUseLoyaltyRewards {
  applyReward({ cartId, rewardBenefitId }: IRewardAction): void;
  unApplyReward({ cartId, rewardBenefitId }: IRewardAction): void;
  /** An object container applied reward ids and the number of times they have been applied */
  appliedLoyaltyRewards: IAppliedRewards;
  /** An object containing applied reward ids and the number of times they have been applied */
  appliedLoyaltyRewardsArray: IParsedLoyaltyReward[] | null;
  /** Pending loyalty point balance minus applied reward balance */
  stagedCartPoints: number;
  /** Available loyalty rewards to be applied at checkout */
  availableLoyaltyRewardsMap: AvailableRewards;
  /** Create and set available loyalty rewards map to be used at checkout */
  createAvailableLoyaltyRewardsMap(rewards: NonNullable<LoyaltyEngineRewards>): void;
  /** Resets all loyalty reward state */
  resetLoyaltyRewardState(): void;
  totalTimesRewardApplied: IParseAppliedRewards['timesRewardApplied'];
  /** Completely removes staged applied reward based on cartId */
  removeAppliedReward({ cartId, rewardBenefitId }: IRewardAction): void;
  setIsPricingRewardApplication: Dispatch<SetStateAction<boolean>>;
  isPricingRewardApplication: boolean;
  getAvailableRewardFromCartEntry: (cartEntry: ICartEntry) => IRewardFragment | undefined;
}
export interface IUseLoyaltyRewardsFeedback {
  loading: boolean;
  error?: ApolloError;
}

export interface IRewardAction {
  cartId: string;
  rewardBenefitId: string;
}

export interface ICartEntryAdapter {
  sanityId: string;
  lineId: string;
  price: number;
  quantity: number;
  children?: ICartEntryAdapter[];
}
// Swap type guard
export const isSwap = (benefit: IBenefit): benefit is IBenefitSwap => {
  return (benefit as IBenefitSwap)?.value && benefit.type === BenefitType.SWAP;
};
export type NotificationDetails = INotificationDetailsFragment;
export interface IUseLoyaltyNotifications {
  notifications: NotificationDetails[];
  loadingNotifications: boolean;
}
export type LoyaltyNotifications = (loyaltyUser: LoyaltyUser | null) => IUseLoyaltyNotifications;

export type RedeemableItem = {
  readonly id: string;
};

export type RedeemableItems = readonly (RedeemableItem | null)[] | null;
